import { ErrorMessage, Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import BasicCheckbox from '../../../../../../components/BasicCheckbox';
import DateSelect from '../../../../../../components/DateSelect';
import HorizontalLoader from '../../../../../../components/HorizontalLoader';
import LoadMask from '../../../../../../components/LoadMask';
import { ANIMATION_TIMEOUT_APPEAR } from '../../../../../../constants/animation';
import ENDPOINT from '../../../../../../constants/endpoints';
import Select from '../../../../../../GbsUI/UI/Select';
import AsyncSelect from '../../../../../../GbsUI/UI/Select/async';
import useApi from '../../../../../../hooks/useApi';
import { useTaxonomy } from '../../../../../../hooks/useTaxonomy';
import AsyncCreatableSelectBox from '../AsyncCreatableSelectBox';
import Controls from '../Controls';
import SelectBox from '../SelectBox';
import TextArea from '../TextArea';
import {
  processGetFormResponseData,
  processPostFormRequestData,
} from './helpers';
import { EditPositionFormValidationSchema } from './validate';

import './styles.css';

const EditPositionForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params,
    endpoint,
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    init: false,
    isRecruiter: params.isRecruiter,
    fields: {
      roleId: null,
      positionId: null,
      industryId: null,
      groupId: null,
      firmId: null,
      cityId: null,
      description: '',
      present: false,
      active: true,
      approved: { value: 1, label: 'Yes' },
      addNewsfeedItem: false,
      sendEmail: false,
      sendDeclineEmail: false,
      startDate: null,
      endDate: null,
    },
    options: {
      roleId: [],
      positionId: [],
      groupId: [],
      industryId: [],
      date: [],
      approved: [],
    },
    settings: {
      hasGroups: false,
      groupTitle: null,
      hideIndustry: false,
    },
  });

  const [options, setOptions] = useState({
    roleId: [],
    positionId: [],
    groupId: [],
    industryId: [],
    date: { year: [], month: [] },
  });
  const [citiesSearchLoading, setCitiesSearchLoading] = useState(false);
  const { data: titles, isLoading: titlesLoading } = useTaxonomy('TITLES', {}, []);
  const { data: cities, isLoading: citiesLoading } = useTaxonomy('CITIES', {}, []);
  const { data: roles, isLoading: rolesLoading } = useTaxonomy('ROLES', {}, []);
  const { data: groups, isLoading: groupsLoading } = useTaxonomy('GROUPS', {
    role: state?.fields?.roleId?.value,
  }, []);
  const { data: industries, isLoading: industriesLoading } = useTaxonomy('INDUSTRIES', {}, []);

  const mapCities = useCallback(response => {
    return response.map(group => {
      return {
        label: group.name,
        options: group.options.map(city => ({
          value: city.id,
          label: city.name,
        })),
      };
    });
  }, []);

  const citiesOptions = useMemo(() => {
    return mapCities(cities);
  }, [cities, mapCities]);

  const loadCities = useCallback(
    s => {
      setCitiesSearchLoading(true);

      return new Promise(resolve => {
        api.get(
          ENDPOINT.TAXONOMIES['CITIES'],
          { s },
          {
            handleError: () => {
              resolve([]);
            },
            handleSuccess: data => {
              console.log(mapCities(data));
              resolve(mapCities(data));
            },
            handleComplete: () => {
              setCitiesSearchLoading(false);
            },
          }
        );
      });
    },
    [api, mapCities]
  );

  useEffect(() => {
    api.get(
      endpoint.getForm,
      { id: params.id, cId: params.cId },
      {
        handleError: handleError,
        handleSuccess: data => {
          const processedData = processGetFormResponseData(data);

          setState(prevState => ({
            ...prevState,
            ...processedData,
            fields: processedData.fields
              ? { ...prevState.fields, ...processedData.fields }
              : { ...prevState.fields },
            init: true,
          }));

          setOptions(processedData.options);
        },
      }
    );
  }, []);

  const [showSendEmail, showSendDeclineEmail, showAddNewsfeed] = useMemo(() => {
    return [
      !params.id ||
        (state.fields.approved && state.fields.approved.value !== 1),
      state.fields.approved && state.fields.approved.value !== 2,
      !state.fields.active,
    ];
  }, [state.fields]);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const data = processPostFormRequestData(
      {
        ...values,
        id: params.id || null,
        cId: params.cId || null,
      },
      params.isMasterAccount
    );

    api.post(endpoint.saveForm, data, {
      handleForbidden: handleError,
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: () => {
        proceed(
          data.present &&
            state.isRecruiter &&
            ((!isEqual(state.fields.firmId, values.firmId) &&
              state.fields.present) ||
              !state.fields.present)
        );
      }
    });
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          validationSchema={EditPositionFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {form => (
            <form onSubmit={handleSubmit} className="form__content">
              {form.isSubmitting && <LoadMask />}

              <div style={{ marginBottom: '20px' }}>
                <Select
                  label={
                    <span
                      className={
                        'lazy-load-select__label-text lazy-load-select__label-text--required'
                      }
                    >
                      ROLE
                    </span>
                  }
                  options={roles}
                  isLoading={rolesLoading}
                  value={form.values?.roleId || ''}
                  onChange={v => {
                    form.setFieldValue('roleId', v);
                    form.setFieldValue('groupId', null);
                    setState(prevState => ({
                      ...prevState,
                      fields: {
                        ...prevState.fields,
                        roleId: v,
                      }
                    }));
                  }}
                  onBlur={() => {
                    form.setFieldTouched('roleId', true);
                  }}
                />
                <ErrorMessage
                  name={'roleId'}
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              {groups.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <Select
                    label={
                      <span
                        className={
                          'lazy-load-select__label-text lazy-load-select__label-text--required'
                        }
                      >
                        GROUP
                      </span>
                    }
                    options={groups}
                    value={form.values?.groupId || ''}
                    isLoading={groupsLoading}
                    onChange={v => {
                      form.setFieldValue('groupId', v);
                    }}
                    onBlur={() => {
                      form.setFieldTouched('groupId', true);
                    }}
                    isMulti
                  />
                  <ErrorMessage
                    name={'groupId'}
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <Select
                  label={
                    <span
                      className={
                        'lazy-load-select__label-text lazy-load-select__label-text--required'
                      }
                    >
                      TITLE
                    </span>
                  }
                  options={titles}
                  isLoading={titlesLoading}
                  value={form.values?.positionId || ''}
                  onChange={v => {
                    form.setFieldValue('positionId', v);
                  }}
                  onBlur={() => {
                    form.setFieldTouched('positionId', true);
                  }}
                />
                <ErrorMessage
                  name={'positionId'}
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <AsyncCreatableSelectBox
                url={endpoint.getFirms}
                name="firmId"
                labelText="Firm"
                value={form.values.firmId}
                required={true}
                isValid={
                  (!form.errors.firmId &&
                    !!form.touched.firmId) ||
                  !form.touched.firmId
                }
                onChange={form.setFieldValue}
                onBlur={form.setFieldTouched}
              />

              <div style={{ marginBottom: '20px' }}>
                <Select
                  label={'Industry'}
                  options={industries}
                  isLoading={industriesLoading}
                  value={form.values?.industryId || ''}
                  onChange={v => {
                    form.setFieldValue('industryId', v);
                  }}
                  onBlur={() => {
                    form.setFieldTouched('industryId', true);
                  }}
                  isMulti
                />
                <ErrorMessage
                  name={'industryId'}
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <AsyncSelect
                  label={
                    <span
                      className={
                        'lazy-load-select__label-text lazy-load-select__label-text--required'
                      }
                    >
                      CITY
                    </span>
                  }
                  defaultOptions={citiesOptions}
                  isLoading={citiesLoading || citiesSearchLoading}
                  loadOptions={loadCities}
                  value={form.values.cityId}
                  onChange={v => {
                    form.setFieldValue('cityId', v);
                  }}
                  onBlur={() => {
                    form.setFieldTouched('cityId', true);
                  }}
                  isSearchable
                />
                <ErrorMessage
                  name={'cityId'}
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <TextArea
                name="description"
                value={form.values.description}
                labelText="Description"
                placeholder="0 characters of the 2000 character limit"
                isValid={
                  (!form.errors.description && !!form.touched.description) ||
                  !form.touched.description
                }
                required={false}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                rows={10}
                resize={false}
              />

              <div className="form__group form__group--compact">
                <DateSelect
                  required
                  isLast={true}
                  label="Start Date"
                  name="startDate"
                  controlFunc={form.setFieldValue}
                  touchFunc={form.setFieldTouched}
                  values={form.values.startDate}
                  yearOptions={options.date.year}
                  isValid={
                    (!form.errors.startDate && !!form.touched.startDate) ||
                    !form.touched.startDate
                  }
                />

                {(form.errors.StartMonth || form.errors.startDate) && (
                  <div className="form__error">
                    {form.errors.StartMonth || form.errors.startDate}
                  </div>
                )}
              </div>

              {!form.values.present && (
                <div className="form__group form__group--compact">
                  <DateSelect
                    required
                    isLast={true}
                    label="End Date"
                    name="endDate"
                    controlFunc={form.setFieldValue}
                    touchFunc={form.setFieldTouched}
                    values={form.values.endDate}
                    yearOptions={options.date.year}
                    isValid={
                      (!form.errors.endDate && !!form.touched.endDate) ||
                      !form.touched.endDate
                    }
                  />

                  <ErrorMessage
                    name="endDate"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div className="form__group form__group--compact">
                <div className="form__row">
                  <BasicCheckbox
                    value={form.values.present}
                    singleRow
                    checked={form.values.present}
                    title="Present"
                    name="present"
                    controlFunc={form.handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>
              </div>

              {form.values.present && (
                <>
                  {!params.isMasterAccount &&
                    state.isRecruiter &&
                    ((!isEqual(
                      state.fields.firmId,
                      form.values.firmId
                    ) &&
                      state.fields.present) ||
                      !state.fields.present) && (
                      <div className="form__group form__group--compact">
                        <span className="position-form__warning">
                          After adding the <strong>current position</strong>,{' '}
                          {props.params.cId ? `this ` : `your `} recruiter
                          account will be blocked as long as the administrator
                          does not confirm the position.
                        </span>
                      </div>
                    )}
                </>
              )}

              {params.isMasterAccount && (
                <>
                  <SelectBox
                    name="approved"
                    value={form.values.approved}
                    labelText="approved"
                    isValid={
                      (!form.errors.approved && !!form.touched.approved) ||
                      !form.touched.approved
                    }
                    required={true}
                    options={state.options?.approved || []}
                    onChange={form.setFieldValue}
                    onBlur={form.setFieldTouched}
                  />
                  <div className="form__group form__group--compact">
                    {showAddNewsfeed &&
                      form.values.present &&
                      form.values.active &&
                      form.values.approved &&
                      form.values.approved.value === 1 && (
                        <div className="form__row">
                          <BasicCheckbox
                            value={form.values.AddNewsfeedItem}
                            singleRow
                            checked={!!form.values.AddNewsfeedItem}
                            title="Add newsfeed item"
                            name="AddNewsfeedItem"
                            controlFunc={form.handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      )}
                    {showSendDeclineEmail &&
                      form.values.approved &&
                      form.values.approved.value === 2 && (
                        <div className="form__row">
                          <BasicCheckbox
                            value={form.values.SendDeclineEmail}
                            singleRow
                            checked={!!form.values.SendDeclineEmail}
                            title="Send decline notification"
                            name="SendDeclineEmail"
                            controlFunc={form.handleChange}
                            labelStyles={{
                              fontFamily: 'Noto Sans, sans-serif',
                            }}
                          />
                        </div>
                      )}
                  </div>
                </>
              )}

              <Controls
                onSubmit={form.handleSubmit}
                onClose={dismiss}
                submitBtnText={params.id ? 'Update Position' : 'Add Position'}
              />
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

export default EditPositionForm;
